import React, { useEffect, useMemo, useRef, useState } from "react";
import ax from "../../config/axios";
import { useAppSelector } from "../../hooks/redux";

interface User {
  id: string;
  name: string;
  email: string;
  roles: string[];
  roleIds: string[];
  createdAt?: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
  color: string;
}

const normalizeRoleIds = (roleIds: string[]) =>
  [...new Set(roleIds)].sort().join("|");

export default function UserManagement() {
  const { scopes } = useAppSelector((state) => state.auth);
  const isAdmin = scopes.includes("admin");

  const [users, setUsers] = useState<User[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRoleFilterIds, setSelectedRoleFilterIds] = useState<string[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [draftRoleIds, setDraftRoleIds] = useState<string[]>([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const selectedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    selectedUserIdRef.current = selectedUserId;
  }, [selectedUserId]);

  const fetchUsers = async (): Promise<User[]> => {
    const response = await ax.get("data/user-permissions");
    const mappedUsers: User[] = response.data.map((user: any): User => ({
      id: user.UserID,
      name:
        user.FirstName || user.LastName
          ? `${user.FirstName ?? ""} ${user.LastName ?? ""}`.trim()
          : "",
      email: user.EmailAddress,
      roles: user.scopes ?? [],
      roleIds: user.scope_ids ?? [],
      createdAt: user.CreatedAt,
    }));
    setUsers(mappedUsers);
    return mappedUsers;
  };

  const fetchRoles = async (): Promise<Role[]> => {
    const response = await ax.get("data/permission-levels");
    const mappedRoles: Role[] = response.data.map((role: any): Role => ({
      id: role.ScopeID,
      name: role.ScopeName,
      description: role.ScopeDescription || "",
      color: role.ScopeColor || "bg-secondary",
    }));
    setAvailableRoles(mappedRoles);
    return mappedRoles;
  };

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        await Promise.all([fetchUsers(), fetchRoles()]);
      } catch (requestError: any) {
        const errorMessage =
          requestError?.response?.data?.error ||
          "Failed to load user role data. Please refresh and try again.";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isAdmin]);

  useEffect(() => {
    if (!users.length) {
      setSelectedUserId(null);
      setDraftRoleIds([]);
      return;
    }

    if (!selectedUserId || !users.some((user) => user.id === selectedUserId)) {
      setSelectedUserId(users[0].id);
      setDraftRoleIds([...users[0].roleIds]);
    }
  }, [users, selectedUserId]);

  const selectedUser = useMemo(
    () => users.find((user) => user.id === selectedUserId) ?? null,
    [users, selectedUserId],
  );

  const isDirty = useMemo(() => {
    if (!selectedUser) {
      return false;
    }
    return normalizeRoleIds(draftRoleIds) !== normalizeRoleIds(selectedUser.roleIds);
  }, [draftRoleIds, selectedUser]);

  const getRoleById = (roleId: string) =>
    availableRoles.find((role) => role.id === roleId);

  const getRoleNameById = (roleId: string) => getRoleById(roleId)?.name || roleId;
  const getRoleColorById = (roleId: string) => getRoleById(roleId)?.color || "bg-secondary";

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRoleFilter =
        selectedRoleFilterIds.length === 0 ||
        selectedRoleFilterIds.some((roleId) => user.roleIds.includes(roleId));
      return matchesSearch && matchesRoleFilter;
    });
  }, [users, searchTerm, selectedRoleFilterIds]);

  const toggleRoleFilter = (roleId: string) => {
    setSelectedRoleFilterIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId],
    );
  };

  const handleSelectUser = (user: User) => {
    if (isDirty && selectedUserId !== user.id) {
      setError("You have unsaved changes. Save or discard them before selecting another user.");
      return;
    }

    setSelectedUserId(user.id);
    setDraftRoleIds([...user.roleIds]);
    setSaveSuccess(null);
    setError(null);
  };

  const handleDraftRoleToggle = (roleId: string) => {
    setDraftRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId],
    );
    setSaveSuccess(null);
  };

  const handleDiscardChanges = () => {
    if (!selectedUser) {
      return;
    }
    setDraftRoleIds([...selectedUser.roleIds]);
    setSaveSuccess(null);
    setError(null);
  };

  const handleSaveUserRoles = async () => {
    if (!selectedUser || formSubmitting || !isDirty) {
      return;
    }

    const requestUserId = selectedUser.id;
    const previousUsers = users;
    const optimisticRoleIds = [...draftRoleIds];

    setFormSubmitting(true);
    setError(null);
    setSaveSuccess(null);

    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === requestUserId
          ? {
            ...user,
            roleIds: optimisticRoleIds,
            roles: optimisticRoleIds.map((roleId) => getRoleNameById(roleId)),
          }
          : user,
      ),
    );

    try {
      await ax.post("data/user-permissions", {
        userId: requestUserId,
        roleIds: optimisticRoleIds,
      });

      const refreshedUsers = await fetchUsers();

      if (selectedUserIdRef.current === requestUserId) {
        const refreshedSelectedUser = refreshedUsers.find(
          (user) => user.id === requestUserId,
        );
        setDraftRoleIds([...(refreshedSelectedUser?.roleIds ?? [])]);
      }

      setSaveSuccess("Permissions updated successfully.");
    } catch (requestError: any) {
      setUsers(previousUsers);

      if (selectedUserIdRef.current === requestUserId) {
        const previousSelectedUser = previousUsers.find(
          (user) => user.id === requestUserId,
        );
        setDraftRoleIds([...(previousSelectedUser?.roleIds ?? [])]);
      }

      const errorMessage =
        requestError?.response?.data?.error ||
        "Failed to update user roles. Please try again.";
      setError(errorMessage);
    } finally {
      setFormSubmitting(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div
      className="card border-0 shadow-sm overflow-hidden d-flex flex-column"
      style={{ height: "calc(100vh - 23rem)", minHeight: 600 }}
    >
      <div className="card-header bg-white border-bottom py-2 py-lg-3">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-2">
          <div>
            <h4 className="mb-1">User Role Management</h4>
            <p className="text-muted mb-0">
              Select a user and update role assignments without leaving this screen.
            </p>
          </div>
          <span className="badge text-bg-light border">
            {filteredUsers.length} visible users
          </span>
        </div>
      </div>

      <div
        className="card-body p-2 p-lg-3 d-flex flex-column overflow-hidden"
        style={{ minHeight: 0 }}
      >
        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div
          className="row gx-2 gy-0 flex-grow-1 mx-0 h-100 overflow-hidden"
          style={{ minHeight: 0 }}
        >
          <div className="col-lg-6 col-xl-7 px-1 d-flex h-100 overflow-hidden" style={{ minHeight: 0 }}>
            <div className="card border flex-grow-1 h-100 overflow-hidden" style={{ minHeight: 0 }}>
              <div className="card-body d-flex flex-column p-2 p-lg-3 overflow-hidden" style={{ minHeight: 0 }}>
                <div className="mb-2">
                  <label htmlFor="user-search" className="form-label fw-semibold">
                    Search users
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-white">Search</span>
                    <input
                      id="user-search"
                      type="text"
                      className="form-control"
                      placeholder="Name or email"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                  </div>
                </div>

                <div className="mb-2">
                  <span className="form-label fw-semibold d-block mb-2">Filter by role</span>
                  <div className="d-flex flex-wrap gap-2">
                    {availableRoles.map((role) => (
                      <button
                        key={role.id}
                        className={`btn btn-sm ${
                          selectedRoleFilterIds.includes(role.id)
                            ? "btn-success"
                            : "btn-outline-success"
                          }`}
                        onClick={() => toggleRoleFilter(role.id)}
                        type="button"
                        title={role.description}
                      >
                        {role.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border rounded overflow-hidden flex-grow-1" style={{ minHeight: 0 }}>
                  <div className="list-group list-group-flush overflow-auto h-100">
                    {!isLoading &&
                      filteredUsers.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          className={`list-group-item list-group-item-action ${selectedUserId === user.id
                              ? "bg-primary-subtle border-primary-subtle text-body"
                              : ""
                            }`}
                          onClick={() => handleSelectUser(user)}
                          disabled={isDirty && selectedUserId !== user.id}
                        >
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <div className="text-start">
                              <div className="fw-semibold">{user.name || user.email}</div>
                              <small className="text-muted">
                                {user.email}
                              </small>
                            </div>
                          </div>
                          <div className="d-flex flex-wrap gap-1 mt-2">
                            {user.roleIds.map((roleId) => (
                              <span
                                key={`${user.id}-${roleId}`}
                                className={`badge rounded-pill ${getRoleColorById(roleId)}`}
                              >
                                {getRoleNameById(roleId)}
                              </span>
                            ))}
                          </div>
                        </button>
                      ))}

                    {!isLoading && filteredUsers.length === 0 && (
                      <div className="text-center p-4">
                        <div className="text-muted fw-semibold">No results</div>
                        <p className="text-muted mb-0 mt-2">No users match the current filters.</p>
                      </div>
                    )}

                    {isLoading && (
                      <div className="d-flex justify-content-center align-items-center p-4">
                        <div className="spinner-border text-success" role="status">
                          <span className="visually-hidden">Loading users...</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {isDirty && (
                  <div className="border-top pt-2 mt-2 small text-muted">
                    Save or discard changes to switch users.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6 col-xl-5 px-1 d-flex h-100 overflow-hidden" style={{ minHeight: 0 }}>
            <div className="card border flex-grow-1 h-100 overflow-hidden" style={{ minHeight: 0 }}>
              <div
                className="card-body p-2 p-lg-3 overflow-hidden d-flex flex-column"
                style={{ minHeight: 0 }}
              >
                {!selectedUser && !isLoading && (
                  <div className="h-100 d-flex flex-column justify-content-center align-items-center text-center">
                    <div className="text-muted fw-semibold">No user selected</div>
                    <h5 className="mt-3 mb-1">Select a user to edit roles</h5>
                    <p className="text-muted mb-0">
                      Choose a user from the left panel to manage permissions.
                    </p>
                  </div>
                )}

                {selectedUser && (
                  <div
                    className="d-flex flex-column flex-grow-1 overflow-hidden"
                    style={{ minHeight: 0 }}
                  >
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 pb-3 border-bottom">
                      <div>
                        <h5 className="mb-1">{selectedUser.name || selectedUser.email}</h5>
                        <p className="mb-1 text-muted">{selectedUser.name ? selectedUser.email : "Name: ---"}</p>
                        <small className="text-muted">
                          Created: {selectedUser.createdAt || "---"}
                        </small>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {isDirty && <span className="badge text-bg-warning">Unsaved changes</span>}
                      </div>
                    </div>

                    {saveSuccess && (
                      <div className="alert alert-success mt-3 mb-0" role="alert">
                        {saveSuccess}
                      </div>
                    )}

                    <div
                      className="pt-3 flex-grow-1 d-flex flex-column overflow-hidden"
                      style={{ minHeight: 0 }}
                    >
                      <h6 className="mb-2">Assigned Roles</h6>
                      {draftRoleIds.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {draftRoleIds.map((roleId) => (
                            <span
                              key={`assigned-${roleId}`}
                              className={`badge rounded-pill ${getRoleColorById(roleId)}`}
                            >
                              {getRoleNameById(roleId)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted mb-3">This user has no assigned roles.</p>
                      )}

                      <h6 className="mb-2">Edit Roles</h6>
                      <div
                        className="flex-grow-1 overflow-auto pe-1 pb-1"
                        style={{ minHeight: 0, scrollbarGutter: "stable" }}
                      >
                        <div className="list-group me-2">
                          {availableRoles.map((role) => {
                            const selected = draftRoleIds.includes(role.id);
                            return (
                              <button
                                key={role.id}
                                type="button"
                                className={`list-group-item list-group-item-action text-start ${selected ? "active border-success bg-success-subtle text-dark" : ""
                                  }`}
                                onClick={() => handleDraftRoleToggle(role.id)}
                                title={role.description}
                              >
                                <div className="d-flex align-items-start gap-2">
                                  <input
                                    className="form-check-input mt-1"
                                    type="checkbox"
                                    checked={selected}
                                    readOnly
                                  />
                                  <div>
                                    <div className="fw-semibold">{role.name}</div>
                                    {role.description && (
                                      <small className={selected ? "text-dark-emphasis" : "text-muted"}>
                                        {role.description}
                                      </small>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-auto pt-3 border-top">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleDiscardChanges}
                        disabled={!isDirty || formSubmitting}
                      >
                        Discard Changes
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleSaveUserRoles}
                        disabled={!isDirty || formSubmitting}
                      >
                        Save Roles
                        {formSubmitting && (
                          <span
                            className="spinner-border spinner-border-sm ms-2"
                            role="status"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
